import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PREDICTION_OPTIONS, PREDICTION_LABELS } from "@/lib/constants";
import { formatApiError } from "@/lib/api";

const EMPTY = {
  company_name: "", sicil_no: "", committee_no: "", branch: "Merkez", status: "faal",
  current_vote_prediction_2026: "", contact_person: "", phone: "", city: "",
  istanbul_ticaret_sicil_no: "", sector_tags: "",
};

export function AddFirmDialog({ open, onOpenChange, committees = [], branches = [], onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const branchOpts = React.useMemo(() => {
    const list = (branches || []).filter((b) => b && b !== "Merkez");
    return ["Merkez", ...list];
  }, [branches]);

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error("Firma ünvanı zorunlu"); return; }
    if (!form.sicil_no.trim()) { toast.error("DTO Sicil No zorunlu"); return; }
    if (!form.committee_no) { toast.error("Komite seçin"); return; }
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        sicil_no: form.sicil_no.trim(),
        committee_no: Number(form.committee_no),
        branch: form.branch || "Merkez",
        status: form.status,
        current_vote_prediction_2026: form.current_vote_prediction_2026 || null,
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
        istanbul_ticaret_sicil_no: form.istanbul_ticaret_sicil_no.trim() || null,
        sector_tags: form.sector_tags ? form.sector_tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      const { data } = await api.post("/voters", payload);
      toast.success(`${data.company_name} eklendi (Komite #${data.committee_no})`);
      setForm(EMPTY);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Firma eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) setForm(EMPTY); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-firm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-600" /> Yeni Üye Firma Ekle
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Firma Ünvanı <span className="text-red-500">*</span></Label>
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Örn: ARKAS DENİZCİLİK A.Ş." data-testid="addfirm-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">DTO Sicil No <span className="text-red-500">*</span></Label>
              <Input value={form.sicil_no} onChange={(e) => set("sicil_no", e.target.value)} placeholder="DTO sicil numarası" data-testid="addfirm-sicil" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Komite <span className="text-red-500">*</span></Label>
              <Select value={form.committee_no ? String(form.committee_no) : ""} onValueChange={(v) => set("committee_no", v)}>
                <SelectTrigger data-testid="addfirm-committee"><SelectValue placeholder="Komite seç" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {committees.map((c) => (
                    <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} · {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Şube</Label>
              <Select value={form.branch} onValueChange={(v) => set("branch", v)}>
                <SelectTrigger data-testid="addfirm-branch"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {branchOpts.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Durum</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="addfirm-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="faal">FAAL</SelectItem>
                  <SelectItem value="aski">ASKI</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">2026 Tahmini (opsiyonel)</Label>
            <Select value={form.current_vote_prediction_2026 || "none"} onValueChange={(v) => set("current_vote_prediction_2026", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="addfirm-prediction"><SelectValue placeholder="Seçilmedi" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">İşaretsiz</SelectItem>
                {PREDICTION_OPTIONS.map((p) => <SelectItem key={p} value={p}>{PREDICTION_LABELS[p] || p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">İlgili Kişi</Label>
              <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Ad Soyad" data-testid="addfirm-contact" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Telefon</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xx..." data-testid="addfirm-phone" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Şehir</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="addfirm-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">İTO/Tic. Sicil No (ops.)</Label>
              <Input value={form.istanbul_ticaret_sicil_no} onChange={(e) => set("istanbul_ticaret_sicil_no", e.target.value)} data-testid="addfirm-ito" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Sektörler (virgülle ayırın)</Label>
            <Input value={form.sector_tags} onChange={(e) => set("sector_tags", e.target.value)} placeholder="Denizcilik, Lojistik" data-testid="addfirm-sectors" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="addfirm-cancel">İptal</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-600/90" onClick={submit} disabled={saving} data-testid="addfirm-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Firma Ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
